import AddIcon from '@mui/icons-material/Add';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Autocomplete,
    Box,
    Button,
    Chip,
    FormControl,
    FormControlLabel,
    InputLabel,
    List,
    ListItem,
    MenuItem,
    Paper,
    Select,
    Slider,
    Stack,
    Switch,
    TextField,
    Tooltip,
    Typography,
    useTheme,
} from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import {
    Bar,
    CartesianGrid,
    ComposedChart,
    Legend,
    Line,
    ReferenceLine,
    ResponsiveContainer,
    Tooltip as RechartsTooltip,
    XAxis,
    YAxis,
} from 'recharts';
import EditTrackingService from '../../services/EditTrackingService';

const MIN_VIDEOS_FOR_TREND = 3;

const movingAverage = (points, windowSize, key) => {
    return points.map((point, index) => {
        const start = Math.max(0, index - windowSize + 1);
        const window = points.slice(start, index + 1);
        const sum = window.reduce((acc, p) => acc + p[key], 0);
        return sum / window.length;
    });
};

const dayKeyFor = (isoDate) => new Date(isoDate).toISOString().slice(0, 10);

// Aggregates per-video points into one point per calendar day (averaging that day's
// videos) so the smoothing window spans N days rather than N videos - otherwise a
// single day with many completed videos would dominate the moving average while a
// sparse day barely registers.
const buildDailySmoothing = (points, windowSize) => {
    const dayOrder = [];
    const byDay = new Map();
    points.forEach((point) => {
        const day = dayKeyFor(point.completedDate);
        if (!byDay.has(day)) {
            byDay.set(day, { textSum: 0, timingSum: 0, count: 0 });
            dayOrder.push(day);
        }
        const bucket = byDay.get(day);
        bucket.textSum += point.textEditRate;
        bucket.timingSum += point.timingEditRate;
        bucket.count += 1;
    });

    const dailyPoints = dayOrder.map((day) => {
        const bucket = byDay.get(day);
        return {
            textEditRate: bucket.textSum / bucket.count,
            timingEditRate: bucket.timingSum / bucket.count,
        };
    });

    const textAvgByDay = movingAverage(dailyPoints, windowSize, 'textEditRate');
    const timingAvgByDay = movingAverage(dailyPoints, windowSize, 'timingEditRate');

    const smoothedByDay = new Map();
    dayOrder.forEach((day, index) => {
        smoothedByDay.set(day, { textEditRateAvg: textAvgByDay[index], timingEditRateAvg: timingAvgByDay[index] });
    });
    return smoothedByDay;
};

const EditTrendsTab = () => {
    const theme = useTheme();
    const [dateRange, setDateRange] = useState('last90days');
    const [rawPoints, setRawPoints] = useState([]);
    const [loading, setLoading] = useState(true);
    const [windowSize, setWindowSize] = useState(10);
    const [showRaw, setShowRaw] = useState(false);
    const [markers, setMarkers] = useState([]);
    const [markerDate, setMarkerDate] = useState('');
    const [markerLabel, setMarkerLabel] = useState('');
    const [markerDescription, setMarkerDescription] = useState('');
    const [editingMarkerId, setEditingMarkerId] = useState(null);

    useEffect(() => {
        let cancelled = false;
        const load = async () => {
            setLoading(true);
            const trends = await EditTrackingService.getTrends(dateRange);
            if (!cancelled) {
                setRawPoints(Array.isArray(trends) ? trends : []);
                setLoading(false);
            }
        };
        load();
        return () => {
            cancelled = true;
        };
    }, [dateRange]);

    useEffect(() => {
        let cancelled = false;
        const loadMarkers = async () => {
            const savedMarkers = await EditTrackingService.getDeploymentMarkers();
            if (!cancelled) {
                setMarkers(Array.isArray(savedMarkers) ? savedMarkers : []);
            }
        };
        loadMarkers();
        return () => {
            cancelled = true;
        };
    }, []);

    const chartData = useMemo(() => {
        const smoothedByDay = buildDailySmoothing(rawPoints, windowSize);
        return rawPoints.map((point) => {
            const smoothed = smoothedByDay.get(dayKeyFor(point.completedDate));
            return {
                ...point,
                completedDateLabel: new Date(point.completedDate).toLocaleDateString(),
                textEditRateAvg: smoothed.textEditRateAvg,
                timingEditRateAvg: smoothed.timingEditRateAvg,
            };
        });
    }, [rawPoints, windowSize]);

    const nearestLabelFor = (isoDate) => {
        if (!isoDate || chartData.length === 0) return null;
        const target = new Date(isoDate).getTime();
        let closest = chartData[0];
        let closestDiff = Math.abs(new Date(closest.completedDate).getTime() - target);
        for (const point of chartData) {
            const diff = Math.abs(new Date(point.completedDate).getTime() - target);
            if (diff < closestDiff) {
                closest = point;
                closestDiff = diff;
            }
        }
        return closest.completedDateLabel;
    };

    const handleSaveMarker = async () => {
        if (!markerDate || !markerLabel.trim()) return;
        const date = markerDate;
        const label = markerLabel.trim();
        const description = markerDescription.trim();
        if (editingMarkerId) {
            const updated = await EditTrackingService.updateDeploymentMarker(editingMarkerId, date, label, description);
            if (updated) {
                setMarkers((prev) => prev.map((marker) => (marker.id === editingMarkerId ? updated : marker)));
            }
        } else {
            const created = await EditTrackingService.createDeploymentMarker(date, label, description);
            if (created) {
                setMarkers((prev) => [...prev, created]);
            }
        }
        setMarkerDate('');
        setMarkerLabel('');
        setMarkerDescription('');
        setEditingMarkerId(null);
    };

    const handleSelectExistingMarker = (marker) => {
        if (!marker) {
            setEditingMarkerId(null);
            setMarkerDate('');
            setMarkerLabel('');
            setMarkerDescription('');
            return;
        }
        setEditingMarkerId(marker.id);
        setMarkerDate(marker.date || '');
        setMarkerLabel(marker.label);
        setMarkerDescription(marker.description || '');
    };

    const handleRemoveMarker = async (id) => {
        const removed = await EditTrackingService.deleteDeploymentMarker(id);
        if (removed) {
            setMarkers((prev) => prev.filter((marker) => marker.id !== id));
            if (editingMarkerId === id) {
                handleSelectExistingMarker(null);
            }
        }
    };

    if (loading) {
        return (
            <Paper variant="outlined" sx={{ p: 3 }}>
                <Typography color="text.secondary" align="center">Loading trends...</Typography>
            </Paper>
        );
    }

    if (rawPoints.length === 0) {
        return (
            <Paper variant="outlined" sx={{ p: 3 }}>
                <Typography color="text.secondary" align="center">No data available</Typography>
            </Paper>
        );
    }

    if (rawPoints.length < MIN_VIDEOS_FOR_TREND) {
        return (
            <Paper variant="outlined" sx={{ p: 3 }}>
                <Typography color="text.secondary" align="center">
                    Not enough data in this range to show a trend
                </Typography>
            </Paper>
        );
    }

    return (
        <Paper variant="outlined" sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ mb: 0.5 }}>Model Quality Trend</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Text and timing edit rates (edits per minute of video), one point per video, ordered by completion date.
            </Typography>

            <Accordion variant="outlined" sx={{ mb: 3 }} disableGutters>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography variant="body2" color="text.secondary">
                        What is this measuring, and how accurate is it?
                    </Typography>
                </AccordionSummary>
                <AccordionDetails>
                    <Typography variant="body2" sx={{ mb: 1.5 }}>
                        For every video, the platform sums up the text and timing edits made across all
                        editing sessions on that video, then divides by the video&apos;s duration in minutes.
                        That gives a <strong>rate</strong> — edits per minute of content — rather than a
                        raw count, so a 5-minute video and a 60-minute video can be compared fairly.
                        Points are plotted in the order their videos finished processing, so the line
                        tracks how that rate has moved over time, not video length or upload date.
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 1.5 }}>
                        In effect, this is a proxy for <strong>transcription/translation model quality</strong>:
                        if the model needs less manual correction, the rate goes down. The deployment
                        markers above let you line up rate changes with when a new model or pipeline
                        version went live. The smoothed line (moving average) is what to trust for the
                        overall direction — individual points are noisy since editors sometimes make
                        optional/stylistic edits that aren&apos;t really "model errors."
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 0.5 }}>
                        A few things keep the numbers honest, and a few limits to keep in mind:
                    </Typography>
                    <List dense disablePadding sx={{ listStyleType: 'disc', pl: 3 }}>
                        <ListItem disablePadding sx={{ display: 'list-item' }}>
                            <Typography variant="body2">
                                Multiple editing sessions on the same video are summed together before the rate
                                is calculated, so re-opening a video for edits doesn&apos;t create duplicate points.
                            </Typography>
                        </ListItem>
                        <ListItem disablePadding sx={{ display: 'list-item' }}>
                            <Typography variant="body2">
                                Videos with a missing or unparseable duration are excluded rather than
                                skewing the average with a divide-by-zero or inflated rate.
                            </Typography>
                        </ListItem>
                        <ListItem disablePadding sx={{ display: 'list-item' }}>
                            <Typography variant="body2">
                                Only videos that have finished processing and have at least one tracked edit
                                session are included — videos never opened for editing aren&apos;t represented,
                                so this reflects tracked editing activity, not the full video catalog.
                            </Typography>
                        </ListItem>
                        <ListItem disablePadding sx={{ display: 'list-item' }}>
                            <Typography variant="body2">
                                The smoothed line averages by calendar day first, then smooths across days —
                                so a day where 10 videos finished doesn&apos;t dominate the trend more than a
                                day where only 1 did.
                            </Typography>
                        </ListItem>
                    </List>
                </AccordionDetails>
            </Accordion>

            <FormControl size="small" sx={{ minWidth: 220, mb: 3 }}>
                <InputLabel id="trends-date-range-label">Trends Range</InputLabel>
                <Select
                    labelId="trends-date-range-label"
                    value={dateRange}
                    label="Trends Range"
                    onChange={(e) => setDateRange(e.target.value)}
                >
                    <MenuItem value="last7days">Last 7 Days</MenuItem>
                    <MenuItem value="last30days">Last 30 Days</MenuItem>
                    <MenuItem value="last90days">Last 90 Days</MenuItem>
                    <MenuItem value="last180days">Last 180 Days</MenuItem>
                    <MenuItem value="all">All Available Data</MenuItem>
                </Select>
            </FormControl>

            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, alignItems: { sm: 'center' }, gap: 3, mb: 3 }}>
                <Box sx={{ minWidth: 260, flex: 1 }}>
                    <Typography variant="body2" gutterBottom>
                        Smoothing: last {windowSize} days
                    </Typography>
                    <Slider
                        value={windowSize}
                        onChange={(e, value) => setWindowSize(value)}
                        min={5}
                        max={20}
                        step={1}
                        marks
                        valueLabelDisplay="auto"
                    />
                </Box>
                <FormControlLabel
                    control={<Switch checked={showRaw} onChange={(e) => setShowRaw(e.target.checked)} />}
                    label="Show raw"
                />
            </Box>

            {markers.length > 0 && (
                <Autocomplete
                    size="small"
                    options={markers}
                    getOptionLabel={(marker) => marker.label}
                    value={markers.find((marker) => marker.id === editingMarkerId) || null}
                    onChange={(e, marker) => handleSelectExistingMarker(marker)}
                    renderInput={(params) => <TextField {...params} label="Select existing marker to edit" />}
                    sx={{ maxWidth: 320, mb: 1 }}
                />
            )}
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'flex-start' }} sx={{ mb: 1 }}>
                <TextField
                    label="Deployment date"
                    type="date"
                    size="small"
                    value={markerDate}
                    onChange={(e) => setMarkerDate(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                />
                <TextField
                    label="Marker label"
                    size="small"
                    value={markerLabel}
                    onChange={(e) => setMarkerLabel(e.target.value)}
                />
                <TextField
                    label="Description"
                    size="small"
                    value={markerDescription}
                    onChange={(e) => setMarkerDescription(e.target.value)}
                    sx={{ minWidth: 220 }}
                />
                <Button
                    variant="outlined"
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={handleSaveMarker}
                    disabled={!markerDate || !markerLabel.trim()}
                >
                    {editingMarkerId ? 'Update marker' : 'Add marker'}
                </Button>
                {editingMarkerId && (
                    <Button
                        variant="text"
                        size="small"
                        onClick={() => handleSelectExistingMarker(null)}
                    >
                        Cancel
                    </Button>
                )}
            </Stack>
            {markers.length > 0 && (
                <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mb: 2 }}>
                    {markers.map((marker) => (
                        <Tooltip key={marker.id} title={marker.description || 'No description'}>
                            <Chip
                                label={marker.label}
                                onDelete={() => handleRemoveMarker(marker.id)}
                                onClick={() => handleSelectExistingMarker(marker)}
                                size="small"
                                color={marker.id === editingMarkerId ? 'primary' : 'default'}
                            />
                        </Tooltip>
                    ))}
                </Stack>
            )}
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                Markers are shared across all users and browsers.
            </Typography>

            <ResponsiveContainer width="100%" height={420}>
                <ComposedChart data={chartData} margin={{ top: 28, right: 24, left: 8, bottom: 8 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} />
                    <XAxis dataKey="completedDateLabel" tick={{ fontSize: 12 }} />
                    <YAxis
                        yAxisId="text"
                        orientation="left"
                        label={{ value: 'Text edits / min', angle: -90, position: 'insideLeft' }}
                        tick={{ fontSize: 12 }}
                    />
                    <YAxis
                        yAxisId="timing"
                        orientation="right"
                        label={{ value: 'Timing edits / min', angle: 90, position: 'insideRight' }}
                        tick={{ fontSize: 12 }}
                    />
                    <YAxis yAxisId="volume" hide />
                    <RechartsTooltip
                        labelFormatter={(label, payload) => payload?.[0]?.payload?.videoTitle || label}
                        formatter={(value, name) => [Number(value).toFixed(2), name]}
                    />
                    <Legend />
                    {markers.map((marker) => {
                        const x = nearestLabelFor(marker.date);
                        if (!x) return null;
                        return (
                            <ReferenceLine
                                key={marker.id}
                                yAxisId="text"
                                x={x}
                                stroke={theme.palette.warning.main}
                                strokeDasharray="4 4"
                                label={{ value: marker.label, position: 'top', fill: theme.palette.warning.main, fontSize: 11 }}
                            />
                        );
                    })}
                    <Bar
                        yAxisId="volume"
                        dataKey="durationMinutes"
                        name="Duration processed (min)"
                        fill={theme.palette.action.hover}
                        barSize={12}
                        opacity={0.5}
                    />
                    {showRaw && (
                        <Line
                            yAxisId="text"
                            type="monotone"
                            dataKey="textEditRate"
                            name="Text edit rate (raw)"
                            stroke={theme.palette.primary.light}
                            strokeOpacity={0.4}
                            dot={false}
                        />
                    )}
                    {showRaw && (
                        <Line
                            yAxisId="timing"
                            type="monotone"
                            dataKey="timingEditRate"
                            name="Timing edit rate (raw)"
                            stroke={theme.palette.secondary.light}
                            strokeOpacity={0.4}
                            dot={false}
                        />
                    )}
                    <Line
                        yAxisId="text"
                        type="monotone"
                        dataKey="textEditRateAvg"
                        name="Text edit rate (smoothed)"
                        stroke={theme.palette.primary.main}
                        strokeWidth={2.5}
                        dot={false}
                    />
                    <Line
                        yAxisId="timing"
                        type="monotone"
                        dataKey="timingEditRateAvg"
                        name="Timing edit rate (smoothed)"
                        stroke={theme.palette.secondary.main}
                        strokeWidth={2.5}
                        dot={false}
                    />
                </ComposedChart>
            </ResponsiveContainer>
        </Paper>
    );
};

export default EditTrendsTab;
